import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '@/config/firebase';
import Logo from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userCity } = useAuth();
  const phoneNumber = location.state?.phoneNumber || '';
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const inputRefs = useRef([]);

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Auto-focus first input
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('');
    while (newOtp.length < 6) newOtp.push('');
    setOtp(newOtp);
    inputRefs.current[5]?.focus();
  };

  // Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const confirmationResult = window.confirmationResult;
      if (!confirmationResult) {
        setError('Session expired. Please try again.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const result = await confirmationResult.confirm(otpCode);
      console.log('OTP verification successful:', result.user);

      // Navigate based on city selection
      if (userCity) {
        navigate('/home');
      } else {
        navigate('/city-selection');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendTimer > 0 || !phoneNumber) return;
    setResendTimer(30);
    setOtp(['', '', '', '', '', '']);
    setError('');
    inputRefs.current[0]?.focus();

    try {
      // Clear existing verifier
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) {}
        window.recaptchaVerifier = null;
      }

      // Create invisible reCAPTCHA for resend
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'resend-recaptcha-container', {
        size: 'invisible'
      });

      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      window.confirmationResult = confirmationResult;
    } catch (err) {
      console.error('Error resending OTP:', err);
      setError('Failed to resend OTP. Please try again.');
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) {}
        window.recaptchaVerifier = null;
      }
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #43A047 0%, #66BB6A 25%, #9CCC65 50%, #CDDC39 75%, #FDD835 100%)'
      }}
      data-testid="otp-verification-page"
    >
      <div id="resend-recaptcha-container"></div>
      {/* Logo */}
      <div className="mb-8 animate-fade-in">
        <Logo size="medium" />
      </div>

      {/* OTP Card */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <h1 className="text-2xl font-bold text-gray-800 text-center mb-2" data-testid="otp-heading">
          Verify OTP
        </h1>
        <p className="text-gray-500 text-center mb-2 text-sm">
          Enter the 6-digit code sent to
        </p>
        <div className="flex items-center justify-center mb-6">
          <p className="text-gray-700 font-medium">{phoneNumber}</p>
          <button
            onClick={() => navigate('/login')}
            className="ml-2 text-green-600 text-sm hover:underline"
            data-testid="change-number-button"
          >
            Change number
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center" data-testid="error-message">
            {error}
          </div>
        )}

        {/* OTP Input Form */}
        <form onSubmit={handleVerifyOTP}>
          <div className="flex justify-center space-x-3 mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || otp.join('').length !== 6}
            className="w-full py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(90deg, #43A047 0%, #66BB6A 50%, #9CCC65 100%)'
            }}
            data-testid="verify-button"
          >
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </button>
        </form>

        {/* Resend OTP */}
        <div className="text-center mt-6">
          {resendTimer > 0 ? (
            <p className="text-gray-500 text-sm">
              Resend code in <span className="font-semibold text-gray-700">{resendTimer}s</span>
            </p>
          ) : (
            <button
              onClick={handleResendOTP}
              className="text-green-600 font-semibold text-sm hover:underline"
              data-testid="resend-button"
            >
              Resend code
            </button>
          )}
        </div>

        {/* Support */}
        <p className="text-xs text-gray-500 text-center mt-6">
          Having trouble? <a href="mailto:mylocalgohelp@gmail.com" className="text-green-600 hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
};

export default OTPVerification;